import { useEffect, useMemo, useRef, useState } from 'react';
import MapGL, { Layer, NavigationControl, Source, type MapLayerMouseEvent, type MapRef } from 'react-map-gl/maplibre';
import type { Map as MaplibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
  type GeoJSONStoreFeatures,
  type HexColor,
} from 'terra-draw';
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter';

import {
  DEFAULT_GEOFENCE_COLOR,
  featureToDraft,
  geofenceToFeature,
  geomBounds,
  GUINEA_VIEW,
  SELECTED_OUTLINE_COLOR,
  type GeofenceDraft,
} from '@/lib/geofences';

import { resolveMapStyle, type Geofence } from '@logistech/ui-shared';

const MAP_STYLE = resolveMapStyle(import.meta.env.VITE_MAP_STYLE);
const FILL_LAYER = 'geofences-fill';

export type DrawMode = 'polygon' | 'circle';

export interface GeofenceMapProps {
  geofences: Geofence[];
  selectedId: string | null;
  /** Clic sur une zone (id) ou dans le vide (null). Ignoré pendant un dessin. */
  onSelect: (id: string | null) => void;
  /** Incrémenté par le parent pour recadrer la carte sur la zone sélectionnée (clic dans la liste). */
  focusRequest?: number;
  /** Dessin en cours ; null = consultation / édition. */
  drawMode: DrawMode | null;
  /** Forme terminée (double-clic / clic sur le dernier point, ou second clic du cercle). */
  onDrawn: (draft: GeofenceDraft) => void;
  /** Poignées d'édition sur la zone sélectionnée (geofences.write). */
  editable: boolean;
  /** Incrémenté par le parent pour annuler les modifications non enregistrées. */
  editRevision?: number;
  /** Géométrie modifiée via les poignées (non enregistrée). */
  onEdit: (id: string, draft: GeofenceDraft) => void;
}

const BRAND = DEFAULT_GEOFENCE_COLOR as HexColor;
const ACCENT = SELECTED_OUTLINE_COLOR as HexColor;
const drawStyles = {
  fillColor: BRAND,
  fillOpacity: 0.3,
  outlineColor: BRAND,
  outlineWidth: 2,
  closingPointColor: ACCENT,
  closingPointWidth: 5,
  closingPointOutlineColor: '#ffffff' as HexColor,
  closingPointOutlineWidth: 2,
};

// Les identifiants des géofences sont des UUID v7 : la stratégie par défaut de terra-draw n'accepte que des v4
const idStrategy = { isValidId: (id: unknown) => typeof id === 'string' && id.length > 0, getId: () => crypto.randomUUID() };

const createDraw = (map: MaplibreMap) =>
  new TerraDraw({
    adapter: new TerraDrawMapLibreGLAdapter({ map }),
    idStrategy,
    modes: [
      new TerraDrawPolygonMode({ styles: drawStyles }),
      new TerraDrawCircleMode({ styles: { fillColor: BRAND, fillOpacity: 0.3, outlineColor: BRAND, outlineWidth: 2 } }),
      new TerraDrawSelectMode({
        allowManualDeselection: false,
        allowManualSelection: false,
        flags: {
          polygon: { feature: { draggable: true, coordinates: { draggable: true, midpoints: true, deletable: true } } },
          circle: { feature: { draggable: true, coordinates: { resizable: 'center-fixed' } } },
        },
        styles: {
          selectedPolygonColor: BRAND,
          selectedPolygonFillOpacity: 0.4,
          selectedPolygonOutlineColor: ACCENT,
          selectedPolygonOutlineWidth: 3,
          selectionPointColor: ACCENT,
          selectionPointWidth: 6,
          selectionPointOutlineColor: '#ffffff' as HexColor,
          selectionPointOutlineWidth: 2,
          midPointColor: BRAND,
          midPointWidth: 4,
        },
      }),
    ],
  });

export function GeofenceMap({
  geofences, selectedId, onSelect, focusRequest = 0, drawMode, onDrawn, editable, editRevision = 0, onEdit,
}: GeofenceMapProps) {
  const mapRef = useRef<MapRef | null>(null);
  const drawRef = useRef<TerraDraw | null>(null);
  const [ready, setReady] = useState(false);
  const [hovered, setHovered] = useState(false);
  // Callbacks lus via ref : les écouteurs terra-draw sont posés une seule fois
  const cb = useRef({ onDrawn, onEdit });
  cb.current = { onDrawn, onEdit };

  const selected = geofences.find((g) => g.id === selectedId) ?? null;
  const editing = editable && !drawMode && selected != null;

  const handleLoad = () => {
    const map = mapRef.current?.getMap() as unknown as MaplibreMap | undefined;
    if (!map || drawRef.current) return;
    const draw = createDraw(map);
    draw.start();
    draw.on('finish', (id, ctx) => {
      const feature = draw.getSnapshotFeature(id) as GeoJSONStoreFeatures | undefined;
      if (!feature) return;
      if (ctx.action === 'draw' && (ctx.mode === 'polygon' || ctx.mode === 'circle')) {
        // La forme reste affichée pendant la saisie du nom ; le parent repasse drawMode à null ensuite
        draw.setMode('static');
        // L'arrêt du mode polygone réactive le zoom au double-clic avant l'événement dblclick qui suit ce clic
        map.doubleClickZoom.disable();
        cb.current.onDrawn(featureToDraft(feature));
      } else if (draw.getMode() === 'select') {
        cb.current.onEdit(String(id), featureToDraft(feature));
      }
    });
    drawRef.current = draw;
    if (import.meta.env.DEV) (window as unknown as { __geofenceMap?: MaplibreMap }).__geofenceMap = map; // smoke Playwright
    setReady(true);
  };

  useEffect(() => () => {
    drawRef.current?.stop();
    drawRef.current = null;
  }, []);

  // Dessin : store vidé, mode activé (le zoom au double-clic est coupé via la prop de MapGL, réappliquée à chaque rendu)
  useEffect(() => {
    const draw = drawRef.current;
    if (!ready || !draw || !drawMode) return;
    draw.clear();
    draw.setMode(drawMode);
  }, [ready, drawMode]);

  // Édition : la zone sélectionnée passe dans terra-draw (poignées) ; resynchronisée après enregistrement ou annulation
  useEffect(() => {
    const draw = drawRef.current;
    if (!ready || !draw || drawMode) return;
    draw.clear();
    if (editing && selected) {
      draw.addFeatures([geofenceToFeature(selected) as GeoJSONStoreFeatures]);
      draw.setMode('select');
      draw.selectFeature(selected.id);
    } else {
      draw.setMode('static');
    }
    // selected.updatedAt : nouvelle géométrie serveur après PATCH
  }, [ready, drawMode, editing, selected?.id, selected?.updatedAt, editRevision]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recadrage demandé par la liste
  useEffect(() => {
    if (!ready || !focusRequest || !selected) return;
    mapRef.current?.fitBounds(geomBounds(selected.geom), { padding: 80, maxZoom: 15, duration: 600 });
  }, [ready, focusRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // La zone en cours d'édition est rendue par terra-draw : retirée de la couche MapLibre
  const data = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: geofences
        .filter((g) => !(editing && g.id === selectedId))
        .map((g) => ({
          type: 'Feature' as const,
          id: g.id,
          geometry: g.geom,
          properties: { id: g.id, color: g.color ?? DEFAULT_GEOFENCE_COLOR, selected: g.id === selectedId },
        })),
    }),
    [geofences, selectedId, editing],
  );

  const handleClick = (e: MapLayerMouseEvent) => {
    if (drawMode) return;
    const id = e.features?.[0]?.properties?.['id'];
    onSelect(typeof id === 'string' ? id : null);
  };

  return (
    <div className="relative h-full w-full" data-testid="geofence-map">
      <MapGL
        ref={mapRef}
        initialViewState={GUINEA_VIEW}
        mapStyle={MAP_STYLE}
        style={{ width: '100%', height: '100%' }}
        interactiveLayerIds={[FILL_LAYER]}
        cursor={drawMode ? 'crosshair' : hovered ? 'pointer' : 'grab'}
        // Le double-clic termine le polygone : il ne doit pas zoomer pendant un dessin
        doubleClickZoom={!drawMode}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={handleClick}
        onLoad={handleLoad}
      >
        <NavigationControl position="top-right" />
        <Source id="geofences" type="geojson" data={data}>
          <Layer
            id={FILL_LAYER}
            type="fill"
            paint={{
              'fill-color': ['get', 'color'],
              'fill-opacity': ['case', ['get', 'selected'], 0.5, 0.3],
            }}
          />
          <Layer
            id="geofences-outline"
            type="line"
            paint={{
              'line-color': ['case', ['get', 'selected'], SELECTED_OUTLINE_COLOR, ['get', 'color']],
              'line-width': ['case', ['get', 'selected'], 3, 1.5],
            }}
          />
        </Source>
      </MapGL>
    </div>
  );
}
